import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Trophy, Star, Award, ChevronDown, ChevronUp } from 'lucide-react';
import { sendEmail } from '../services/emailService';

const UpcomingAwards = () => {
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const totalSteps = 5;
  const [formData, setFormData] = useState({
    // Section 1
    institutionName: '',
    institutionType: '',
    ownershipType: '',
    website: '',
    yearEstablishment: '',
    address: '',
    contactPersonName: '',
    contactEmail: '',
    contactNumber: '',
    // Section 2
    instituteProfile: '',
    vcName: '',
    yearsAssociated: '',
    studentStrength: '',
    facultyStrength: '',
    nonTeachingStaff: '',
    accreditations: '',
    affiliatingBody: '',
    aiPartnerships: '',
    // Section 3
    visionGoals: '',
    aiTechnologies: '',
    aiInitiatives: '',
    facultyDevelopment: '',
    futureExpansion: '',
    whyRecognition: '',
    // Section 4
    videoPresentation: null,
    awardsRecognitions: null,
    instituteLogo: null,
    incorporationCertificate: null,
    digitalAssets: '',
    selfDeclaration: null
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  const awardCategories = {
    'HIGHER EDUCATION CATEGORIES': [
      {
        id: 'ai-integrated',
        name: 'Excellence in AI-Integrated Higher Education',
        description: 'Honors higher education institutions that have strategically integrated Artificial Intelligence (AI) across their academic or administrative, or in research ecosystems to enhance learning outcomes, operational efficiency, and innovation.',
        price: '₹ 20000 +18% GST'
      },
      {
        id: 'future-ready',
        name: 'Outstanding Future-Ready Higher Education Institution',
        description: 'Recognizes a university or institute demonstrating a visionary approach to integrating technology, innovation, and sustainability across academics, research, and campus life.',
        price: '₹ 20000 +18% GST'
      },
      {
        id: 'digital-learning',
        name: 'Excellence in Next-Gen Digital & Blended Learning',
        description: 'Recognizes higher education institutions that have redefined learning through advanced digital, hybrid, and blended pedagogies — integrating AI tools, adaptive platforms, immersive learning environments, and data-driven teaching models',
        price: '₹ 20000 +18% GST'
      }
    ],
    'SCHOOL (K12) CATEGORIES': [
      {
        id: 'k12-tech',
        name: 'Outstanding K-12 School in Technology Integration',
        description: 'Recognizes a school that has demonstrated outstanding innovation and leadership in integrating technology across teaching, learning, and administration.',
        price: '₹ 20000 +18% GST'
      },
      {
        id: 'school-network',
        name: 'Outstanding Technology-Driven School Network (Group/Chain)',
        description: 'Recognizes a school group or chain demonstrating exceptional vision and consistency in implementing technology across campuses.',
        price: '₹ 20000 +18% GST'
      }
    ],
    'EDTECH CATEGORIES': [
      {
        id: 'ai-innovation',
        name: 'Excellence in Generative AI Innovation for Learning & Assessment',
        description: 'Recognizes EdTech companies that are pioneering the use of Generative AI to redefine learning, content creation, and assessment design.',
        price: '₹ 20000 +18% GST'
      },
      {
        id: 'personalized-learning',
        name: 'Excellence in Personalized & Adaptive Learning Technologies',
        description: 'Recognizes EdTech platforms driving innovation in personalized and adaptive learning.',
        price: '₹ 20000 +18% GST'
      }
    ],
    'LEADERSHIP CATEGORIES': [
      {
        id: 'school-leader',
        name: 'Pioneering Leader in Technology-Driven School Transformation',
        description: 'Celebrates K12 school leaders who are driving transformative change through technology adoption.',
        price: '₹ 20000 +18% GST'
      },
      {
        id: 'edtech-leader',
        name: 'Transformative EdTech Leader',
        description: 'Recognizes visionary leaders who have significantly transformed the education technology landscape.',
        price: '₹ 20000 +18% GST'
      }
    ]
  };

  const eventsList = [
    {
      id: 'event-1',
      name: 'TechEdu India Awards 2026',
      date: 'Dec 10, 2026',
      venue: 'New Delhi',
      hero: '/images/awards/award-hero-1.jpg',
      brochure: '/brochures/techedu-2026-brochure.pdf',
      externalNominateUrl: 'https://example.com/nominate/techedu-2026',
      about: 'TechEdu India Awards recognizes outstanding institutions and leaders in education technology and innovation.',
      nominationDeadline: 'Nov 30, 2026',
      awards: [
        { id: 'a1', name: 'Excellence in AI-Integrated Education', icon: '🏆' },
        { id: 'a2', name: 'Outstanding Future-Ready Institution', icon: '🏅' },
        { id: 'a3', name: 'Next-Gen Digital Learning', icon: '🎖️' }
      ]
    },
    {
      id: 'event-2',
      name: 'Global School Innovation Awards 2026',
      date: 'Jan 22, 2027',
      venue: 'Bengaluru',
      hero: '/images/awards/award-hero-2.jpg',
      brochure: '/brochures/global-school-2026-brochure.pdf',
      externalNominateUrl: 'https://example.com/nominate/global-school-2026',
      about: 'Global School Innovation Awards celebrates K12 schools and leaders who are redefining education with technology and innovation.',
      nominationDeadline: 'Jan 10, 2027',
      awards: [
        { id: 'a4', name: 'Outstanding K12 Tech Integration', icon: '🏆' },
        { id: 'a5', name: 'Transformative School Leadership', icon: '🏅' }
      ]
    }
  ];

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setSelectedCategories([]);
    setShowForm(false);
    setCurrentStep(1);
    setCompletedSteps(new Set());
  };

  const toggleCategorySelection = (categoryId, categoryName, categorySection) => {
    const category = { id: categoryId, name: categoryName, section: categorySection };
    const isSelected = selectedCategories.some(cat => cat.id === categoryId);
    
    if (isSelected) {
      setSelectedCategories(prev => prev.filter(cat => cat.id !== categoryId));
    } else {
      setSelectedCategories(prev => [...prev, category]);
    }
  };

  const proceedToNomination = () => {
    if (selectedCategories.length === 0) {
      alert('Please select at least one category to proceed.');
      return;
    }
    setShowForm(true);
    setCurrentCategory(selectedCategories[0]);
  };

  const validateCurrentStep = () => {
    const newErrors = {};
    
    switch(currentStep) {
      case 1: // Basic Information
        if (!formData.institutionName.trim()) newErrors.institutionName = 'Institution name is required';
        if (!formData.institutionType) newErrors.institutionType = 'Institution type is required';
        if (!formData.ownershipType) newErrors.ownershipType = 'Ownership type is required';
        if (!formData.website.trim()) newErrors.website = 'Website is required';
        if (!formData.yearEstablishment.trim()) newErrors.yearEstablishment = 'Year of establishment is required';
        if (!formData.contactPersonName.trim()) newErrors.contactPersonName = 'Contact person name is required';
        if (!formData.contactEmail.trim()) {
          newErrors.contactEmail = 'Contact email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.contactEmail)) {
          newErrors.contactEmail = 'Email is invalid';
        }
        if (!formData.contactNumber.trim()) newErrors.contactNumber = 'Contact number is required';
        break;
      case 2: // About Institution
        if (!formData.instituteProfile.trim()) newErrors.instituteProfile = 'Institute profile is required';
        if (formData.instituteProfile.trim().split(' ').length > 500) {
          newErrors.instituteProfile = 'Institute profile must not exceed 500 words';
        }
        break;
      case 3: // Core Application
        if (!formData.visionGoals.trim()) newErrors.visionGoals = 'Vision and goals are required';
        if (formData.visionGoals.trim().split(' ').length > 2000) {
          newErrors.visionGoals = 'Vision and goals must not exceed 2000 words';
        }
        break;
      case 4: // Supporting Documents
        if (!formData.digitalAssets.trim()) newErrors.digitalAssets = 'Digital assets information is required';
        break;
      case 5: // Declaration
        // Add declaration validation if needed
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const canSubmit = completedSteps.size === totalSteps;

  const renderStepContent = () => {
    switch(currentStep) {
      case 1:
        return (
          <div style={{ padding: '30px', backgroundColor: '#f8fafc', borderRadius: '15px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '25px' }}>
              SECTION 1 - BASIC INFORMATION
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Name of the Institution *
                </label>
                <input
                  type="text"
                  name="institutionName"
                  value={formData.institutionName}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.institutionName ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
                {errors.institutionName && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.institutionName}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Type of Institution *
                  </label>
                  <select
                    name="institutionType"
                    value={formData.institutionType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.institutionType ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select Type</option>
                    <option value="University">University</option>
                    <option value="Deemed University">Deemed University</option>
                    <option value="Institute">Institute</option>
                    <option value="Autonomous College">Autonomous College</option>
                  </select>
                  {errors.institutionType && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.institutionType}</span>}
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Ownership Type *
                  </label>
                  <select
                    name="ownershipType"
                    value={formData.ownershipType}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.ownershipType ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select Ownership</option>
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                    <option value="Deemed">Deemed</option>
                    <option value="Autonomous">Autonomous</option>
                  </select>
                  {errors.ownershipType && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.ownershipType}</span>}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Website *
                  </label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://example.com"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.website ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {errors.website && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.website}</span>}
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Year of Establishment *
                  </label>
                  <input
                    type="number"
                    name="yearEstablishment"
                    value={formData.yearEstablishment}
                    onChange={handleChange}
                    min="1800"
                    max="2025"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.yearEstablishment ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {errors.yearEstablishment && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.yearEstablishment}</span>}
                </div>
              </div>

              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Address
                </label>
                <textarea
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Contact Person Name & Designation *
                  </label>
                  <input
                    type="text"
                    name="contactPersonName"
                    value={formData.contactPersonName}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.contactPersonName ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {errors.contactPersonName && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.contactPersonName}</span>}
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Email ID of Contact Person *
                  </label>
                  <input
                    type="email"
                    name="contactEmail"
                    value={formData.contactEmail}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.contactEmail ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {errors.contactEmail && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.contactEmail}</span>}
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Contact Number of POC *
                  </label>
                  <input
                    type="tel"
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: errors.contactNumber ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                  {errors.contactNumber && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.contactNumber}</span>}
                </div>
              </div>
            </div>
          </div>
        );
      case 2:
        return (
          <div style={{ padding: '30px', backgroundColor: '#f8fafc', borderRadius: '15px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '25px' }}>
              SECTION 2 - ABOUT THE INSTITUTION
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Brief Profile of the Institute (Max 500 words) *
                </label>
                <textarea
                  name="instituteProfile"
                  value={formData.instituteProfile}
                  onChange={handleChange}
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.instituteProfile ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
                <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '5px' }}>
                  {formData.instituteProfile.trim().split(' ').length}/500 words
                </div>
                {errors.instituteProfile && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.instituteProfile}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Name of VC/Head
                  </label>
                  <input
                    type="text"
                    name="vcName"
                    value={formData.vcName}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Years Associated
                  </label>
                  <input
                    type="number"
                    name="yearsAssociated"
                    value={formData.yearsAssociated}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Student Strength
                  </label>
                  <input
                    type="number"
                    name="studentStrength"
                    value={formData.studentStrength}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Faculty Strength
                  </label>
                  <input
                    type="number"
                    name="facultyStrength"
                    value={formData.facultyStrength}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Non-Teaching Staff
                  </label>
                  <input
                    type="number"
                    name="nonTeachingStaff"
                    value={formData.nonTeachingStaff}
                    onChange={handleChange}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 3:
        return (
          <div style={{ padding: '30px', backgroundColor: '#f8fafc', borderRadius: '15px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '25px' }}>
              SECTION 3 - CORE APPLICATION QUESTIONS
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Vision, Goals & Strategic Objectives (Max 2000 words) *
                </label>
                <textarea
                  name="visionGoals"
                  value={formData.visionGoals}
                  onChange={handleChange}
                  rows={8}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.visionGoals ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
                <div style={{ fontSize: '0.8rem', color: '#718096', marginTop: '5px' }}>
                  {formData.visionGoals.trim().split(' ').length}/2000 words
                </div>
                {errors.visionGoals && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.visionGoals}</span>}
              </div>

              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  AI Technologies Implemented
                </label>
                <textarea
                  name="aiTechnologies"
                  value={formData.aiTechnologies}
                  onChange={handleChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  AI Initiatives & Programs
                </label>
                <textarea
                  name="aiInitiatives"
                  value={formData.aiInitiatives}
                  onChange={handleChange}
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div style={{ padding: '30px', backgroundColor: '#f8fafc', borderRadius: '15px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '25px' }}>
              SECTION 4 - SUPPORTING DOCUMENTS
            </h3>
            <div style={{ display: 'grid', gap: '20px' }}>
              <div>
                <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                  Digital Assets & Portfolio Links *
                </label>
                <textarea
                  name="digitalAssets"
                  value={formData.digitalAssets}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Provide links to videos, presentations, websites, or other digital assets"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: errors.digitalAssets ? '2px solid #e53e3e' : '2px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                />
                {errors.digitalAssets && <span style={{ color: '#e53e3e', fontSize: '0.9rem' }}>{errors.digitalAssets}</span>}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Video Presentation
                  </label>
                  <input
                    type="file"
                    name="videoPresentation"
                    onChange={handleChange}
                    accept="video/*"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>

                <div>
                  <label style={{ color: '#2d3748', fontWeight: 600, marginBottom: '8px', display: 'block' }}>
                    Institution Logo
                  </label>
                  <input
                    type="file"
                    name="instituteLogo"
                    onChange={handleChange}
                    accept="image/*"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div style={{ padding: '30px', backgroundColor: '#f8fafc', borderRadius: '15px' }}>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '25px' }}>
              SECTION 5 - DECLARATION
            </h3>
            <div style={{ padding: '20px', backgroundColor: 'white', borderRadius: '10px', border: '2px solid #e2e8f0' }}>
              <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', marginBottom: '20px' }}>
                I hereby declare that all the information provided in this application is true and accurate to the best of my knowledge. 
                I understand that any false information may result in disqualification from the award process.
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="declaration"
                  checked={formData.declaration || false}
                  onChange={(e) => setFormData(prev => ({ ...prev, declaration: e.target.checked }))}
                  style={{ width: '18px', height: '18px' }}
                />
                <span style={{ color: '#2d3748', fontWeight: 600 }}>
                  I agree to the terms and conditions and declare that all information is accurate.
                </span>
              </label>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) {
      alert('Please complete all sections before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus('');
    
    try {
      const emailData = {
        subject: `TechEdu India Awards 2026 - ${currentCategory.name} Nomination`,
        html: `
          <h2>TechEdu India Awards 2026 - Award Nomination</h2>
          <h3>Category: ${currentCategory.name}</h3>
          <h3>Section: ${currentCategory.section}</h3>
          
          <h4>SECTION 1 - BASIC INFORMATION</h4>
          <p><strong>Institution Name:</strong> ${formData.institutionName}</p>
          <p><strong>Institution Type:</strong> ${formData.institutionType}</p>
          <p><strong>Ownership Type:</strong> ${formData.ownershipType}</p>
          <p><strong>Website:</strong> ${formData.website}</p>
          <p><strong>Year of Establishment:</strong> ${formData.yearEstablishment}</p>
          <p><strong>Address:</strong> ${formData.address}</p>
          <p><strong>Contact Person:</strong> ${formData.contactPersonName}</p>
          <p><strong>Contact Email:</strong> ${formData.contactEmail}</p>
          <p><strong>Contact Number:</strong> ${formData.contactNumber}</p>
          
          <h4>SECTION 2 - ABOUT THE INSTITUTION</h4>
          <p><strong>Institute Profile:</strong> ${formData.instituteProfile}</p>
          <p><strong>VC/Head Name:</strong> ${formData.vcName}</p>
          <p><strong>Years Associated:</strong> ${formData.yearsAssociated}</p>
          <p><strong>Student Strength:</strong> ${formData.studentStrength}</p>
          <p><strong>Faculty Strength:</strong> ${formData.facultyStrength}</p>
          <p><strong>Non-Teaching Staff:</strong> ${formData.nonTeachingStaff}</p>
          
          <h4>SECTION 3 - CORE APPLICATION QUESTIONS</h4>
          <p><strong>Vision & Goals:</strong> ${formData.visionGoals}</p>
          <p><strong>AI Technologies:</strong> ${formData.aiTechnologies}</p>
          <p><strong>AI Initiatives:</strong> ${formData.aiInitiatives}</p>
          
          <h4>SECTION 4 - SUPPORTING DOCUMENTS</h4>
          <p><strong>Digital Assets:</strong> ${formData.digitalAssets}</p>
          
          <h4>SECTION 5 - DECLARATION</h4>
          <p><strong>Declaration Accepted:</strong> ${formData.declaration ? 'Yes' : 'No'}</p>
          
          <p><strong>Submitted on:</strong> ${new Date().toLocaleString()}</p>
        `,
        from_name: formData.contactPersonName,
        reply_to: formData.contactEmail
      };
      
      await sendEmail(emailData);
      setSubmitStatus('success');
      setTimeout(() => {
        setShowForm(false);
        setSelectedCategories([]);
        setCurrentCategory(null);
        setCurrentStep(1);
        setCompletedSteps(new Set());
        setFormData({
          institutionName: '', institutionType: '', ownershipType: '', website: '', yearEstablishment: '',
          address: '', contactPersonName: '', contactEmail: '', contactNumber: '', instituteProfile: '',
          vcName: '', yearsAssociated: '', studentStrength: '', facultyStrength: '', nonTeachingStaff: '',
          accreditations: '', affiliatingBody: '', aiPartnerships: '', visionGoals: '', aiTechnologies: '',
          aiInitiatives: '', facultyDevelopment: '', futureExpansion: '', whyRecognition: '',
          videoPresentation: null, awardsRecognitions: null, instituteLogo: null,
          incorporationCertificate: null, digitalAssets: '', selfDeclaration: null, declaration: false
        });
        setSubmitStatus('');
      }, 3000);
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  if (showForm && currentCategory) {
    return (
      <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingTop: '80px' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '40px 20px' }}>
          <button
            onClick={() => setShowForm(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#667eea',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              marginBottom: '30px'
            }}
          >
            <ArrowLeft size={20} />
            Back to Category Selection
          </button>

          <div style={{
            backgroundColor: 'white',
            borderRadius: '20px',
            padding: '50px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '40px' }}>
              <h1 style={{ color: '#2d3748', fontSize: '2rem', fontWeight: 700, marginBottom: '10px' }}>
                {currentCategory.name}
              </h1>
              <p style={{ color: '#667eea', fontSize: '1.1rem', fontWeight: 600 }}>
                [Master Category: {currentCategory.section}]
              </p>
            </div>

            {submitStatus === 'success' && (
              <div style={{
                backgroundColor: '#c6f6d5',
                color: '#22543d',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '25px',
                textAlign: 'center',
                fontWeight: 600
              }}>
                Nomination submitted successfully! We'll review your application and contact you soon.
              </div>
            )}

            {submitStatus === 'error' && (
              <div style={{
                backgroundColor: '#fed7d7',
                color: '#c53030',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '25px',
                textAlign: 'center',
                fontWeight: 600
              }}>
                Submission failed. Please try again.
              </div>
            )}

            <form onSubmit={handleSubmit}>
              {/* Step Progress Indicator */}
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '40px' }}>
                {[1, 2, 3, 4, 5].map((step) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'center' }}>
                    <div style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: completedSteps.has(step) ? '#22c55e' : (currentStep === step ? '#667eea' : '#e2e8f0'),
                      color: completedSteps.has(step) || currentStep === step ? 'white' : '#9ca3af',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}>
                      {completedSteps.has(step) ? '✓' : step}
                    </div>
                    {step < 5 && (
                      <div style={{
                        width: '60px',
                        height: '2px',
                        backgroundColor: completedSteps.has(step) ? '#22c55e' : '#e2e8f0',
                        margin: '0 10px'
                      }} />
                    )}
                  </div>
                ))}
              </div>

              {/* Current Step Content */}
              {renderStepContent()}

              {/* Navigation Buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '40px' }}>
                <button
                  type="button"
                  onClick={handlePrevious}
                  disabled={currentStep === 1}
                  style={{
                    padding: '12px 24px',
                    backgroundColor: currentStep === 1 ? '#e2e8f0' : 'white',
                    color: currentStep === 1 ? '#9ca3af' : '#667eea',
                    border: '2px solid #667eea',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontWeight: 600,
                    cursor: currentStep === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  Previous
                </button>

                <div style={{ fontSize: '1rem', color: '#4a5568', fontWeight: 600 }}>
                  Step {currentStep} of {totalSteps}
                </div>

                {currentStep < totalSteps ? (
                  <button
                    type="button"
                    onClick={handleNext}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#667eea',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Next
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!canSubmit || isSubmitting}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: !canSubmit || isSubmitting ? '#a0aec0' : '#22c55e',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '1rem',
                      fontWeight: 600,
                      cursor: !canSubmit || isSubmitting ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit Nomination'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f8fafc', minHeight: '100vh', paddingTop: '80px' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '40px 20px' }}>
        <Link 
          to="/awards" 
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '8px', 
            color: '#667eea', 
            textDecoration: 'none', 
            marginBottom: '30px', 
            fontWeight: 600 
          }}
        >
          <ArrowLeft size={20} />
          Back to Awards
        </Link>

        <div style={{ textAlign: 'center', marginBottom: '50px' }}>
          <h1 style={{ color: '#2d3748', fontSize: '3rem', fontWeight: 700, marginBottom: '20px' }}>
            Upcoming Awards
          </h1>
          <h2 style={{ color: '#667eea', fontSize: '1.5rem', fontWeight: 600, marginBottom: '20px' }}>
            Explore upcoming award events and start your nomination.
          </h2>
          <p style={{ color: '#718096', fontSize: '1.1rem' }}>
            Choose an event to view details, download the brochure, and nominate your institution or leader.
          </p>
        </div>

        {!selectedEvent ? (
          <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', marginBottom: '50px' }}>
            {eventsList.map((event) => (
              <div
                key={event.id}
                onClick={() => handleSelectEvent(event)}
                style={{
                  cursor: 'pointer',
                  borderRadius: '24px',
                  overflow: 'hidden',
                  boxShadow: '0 20px 50px rgba(15,23,42,0.12)',
                  backgroundColor: 'white',
                  transition: 'transform 0.2s ease'
                }}
              >
                <div style={{
                  height: '240px',
                  backgroundImage: `url(${event.hero})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center'
                }} />
                <div style={{ padding: '28px' }}>
                  <h3 style={{ color: '#1f2937', fontSize: '1.45rem', marginBottom: '12px' }}>{event.name}</h3>
                  <div style={{ color: '#667eea', fontWeight: 700, marginBottom: '14px' }}>{event.date} · {event.venue}</div>
                  <p style={{ color: '#475569', lineHeight: 1.8, marginBottom: '20px' }}>{event.about}</p>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{ backgroundColor: '#eef2ff', color: '#3730a3', padding: '10px 16px', borderRadius: '999px', fontWeight: 700, fontSize: '0.95rem' }}>
                      Deadline {event.nominationDeadline}
                    </span>
                    <span style={{ color: '#0f172a', fontWeight: 700 }}>
                      View details →
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            <div style={{ position: 'relative', borderRadius: '30px', overflow: 'hidden', minHeight: '420px', marginBottom: '40px' }}>
              <div style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: `linear-gradient(180deg, rgba(15,23,42,0.24), rgba(15,23,42,0.88)), url(${selectedEvent.hero})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center'
              }} />
              <div style={{ position: 'relative', zIndex: 2, padding: '60px 40px', color: 'white', display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                <div style={{ marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <span style={{ backgroundColor: 'rgba(255,255,255,0.16)', padding: '10px 16px', borderRadius: '999px', fontSize: '0.95rem', fontWeight: 700 }}>
                    Upcoming Event
                  </span>
                  <span style={{ fontWeight: 600 }}>{selectedEvent.date} · {selectedEvent.venue}</span>
                </div>
                <h1 style={{ fontSize: '3rem', lineHeight: 1.05, marginBottom: '20px', maxWidth: '760px' }}>
                  {selectedEvent.name}
                </h1>
                <p style={{ maxWidth: '720px', color: 'rgba(255,255,255,0.88)', fontSize: '1.05rem', lineHeight: 1.8, marginBottom: '30px' }}>
                  {selectedEvent.about}
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
                  <button type="button" onClick={() => window.open(selectedEvent.brochure, '_blank')} style={{ padding: '14px 26px', backgroundColor: 'white', color: '#0f172a', borderRadius: '999px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                    Download Brochure
                  </button>
                  <button type="button" onClick={() => window.open(selectedEvent.externalNominateUrl, '_blank')} style={{ padding: '14px 26px', backgroundColor: '#22c55e', color: 'white', borderRadius: '999px', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
                    Nominate Now
                  </button>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gap: '30px', gridTemplateColumns: '1fr 360px' }}>
              <div>
                <div style={{ marginBottom: '30px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
                    <div>
                      <h2 style={{ fontSize: '2rem', color: '#1f2937', fontWeight: 700, marginBottom: '12px' }}>
                        About the Event
                      </h2>
                      <p style={{ color: '#4b5563', lineHeight: 1.8 }}>
                        {selectedEvent.about}
                      </p>
                    </div>
                    <div style={{ backgroundColor: '#eef2ff', color: '#3730a3', padding: '20px', borderRadius: '20px', fontWeight: 700 }}>
                      Nomination Deadline
                      <div style={{ marginTop: '6px', fontSize: '1.1rem' }}>{selectedEvent.nominationDeadline}</div>
                    </div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '20px', marginBottom: '40px' }}>
                  {selectedEvent.awards.map((award) => (
                    <div key={award.id} style={{ padding: '24px', borderRadius: '20px', backgroundColor: '#f8fafc', boxShadow: '0 10px 30px rgba(15,23,42,0.08)' }}>
                      <div style={{ fontSize: '2rem', marginBottom: '14px' }}>{award.icon}</div>
                      <div style={{ fontWeight: 700, color: '#111827', marginBottom: '8px' }}>{award.name}</div>
                      <p style={{ color: '#4b5563', lineHeight: 1.7, fontSize: '0.95rem', margin: 0 }}>
                        Recognizing excellence in event categories and awards.
                      </p>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                  <button type="button" onClick={() => window.open(selectedEvent.brochure, '_blank')} style={{ padding: '14px 28px', borderRadius: '999px', backgroundColor: '#667eea', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                    Download Brochure
                  </button>
                  <button type="button" onClick={() => window.open(selectedEvent.externalNominateUrl, '_blank')} style={{ padding: '14px 28px', borderRadius: '999px', backgroundColor: '#22c55e', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                    Nominate Now
                  </button>
                  <button type="button" onClick={() => setSelectedEvent(null)} style={{ padding: '14px 28px', borderRadius: '999px', backgroundColor: '#f1f5f9', color: '#334155', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
                    View Other Events
                  </button>
                </div>
              </div>

              <aside style={{ position: 'sticky', top: '24px' }}>
                <div style={{ padding: '30px', borderRadius: '24px', backgroundColor: 'white', boxShadow: '0 20px 50px rgba(15,23,42,0.08)' }}>
                  <h3 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#1f2937', marginBottom: '15px' }}>Categories & Fees</h3>
                  <div style={{ display: 'grid', gap: '18px' }}>
                    {Object.entries(awardCategories).map(([sectionName, categories]) => (
                      <div key={sectionName}>
                        <div style={{ marginBottom: '12px', fontSize: '1rem', fontWeight: 700, color: '#475569' }}>{sectionName}</div>
                        {categories.map((category) => (
                          <div key={category.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', padding: '14px 16px', borderRadius: '16px', backgroundColor: '#f8fafc' }}>
                            <div style={{ minWidth: 0 }}>
                              <p style={{ margin: 0, fontWeight: 600, color: '#0f172a', fontSize: '0.98rem' }}>{category.name}</p>
                              <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.9rem' }}>{category.description}</p>
                            </div>
                            <span style={{ whiteSpace: 'nowrap', fontWeight: 700, color: '#0f172a' }}>{category.price}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </>
        )}

        {selectedEvent ? (
          <>
            {/* Categories */}
            {Object.entries(awardCategories).map(([sectionName, categories]) => (
          <div key={sectionName} style={{ marginBottom: '50px' }}>
            <div style={{
              backgroundColor: '#667eea',
              color: 'white',
              padding: '20px',
              borderRadius: '15px 15px 0 0',
              fontSize: '1.3rem',
              fontWeight: 700
            }}>
              {sectionName}
              <span style={{ fontSize: '1rem', opacity: 0.9, marginLeft: '10px' }}>
                {categories.length} Sub Categories
              </span>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '0 0 15px 15px',
              boxShadow: '0 5px 20px rgba(0,0,0,0.1)'
            }}>
              {categories.map((category) => (
                <div 
                  key={category.id}
                  style={{
                    padding: '30px',
                    borderBottom: '1px solid #e2e8f0',
                    cursor: 'pointer',
                    backgroundColor: selectedCategories.some(cat => cat.id === category.id) ? '#f0f9ff' : 'white',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => toggleCategorySelection(category.id, category.name, sectionName)}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', gap: '20px' }}>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ 
                        color: '#2d3748', 
                        fontSize: '1.3rem', 
                        fontWeight: 700, 
                        marginBottom: '10px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px'
                      }}>
                        <input
                          type="checkbox"
                          checked={selectedCategories.some(cat => cat.id === category.id)}
                          onChange={(e) => { e.stopPropagation(); toggleCategorySelection(category.id, category.name, sectionName); }}
                          style={{ width: '18px', height: '18px' }}
                        />
                        {category.name}
                      </h3>
                      <p style={{ color: '#4a5568', fontSize: '1rem', lineHeight: '1.6', marginBottom: '15px' }}>
                        {category.description}
                      </p>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{
                        backgroundColor: '#667eea',
                        color: 'white',
                        padding: '8px 16px',
                        borderRadius: '20px',
                        fontSize: '1rem',
                        fontWeight: 600
                      }}>
                        {category.price}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Selected Categories Summary */}
        {selectedCategories.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '30px',
            borderRadius: '15px',
            boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
            marginBottom: '30px'
          }}>
            <h3 style={{ color: '#2d3748', fontSize: '1.5rem', fontWeight: 700, marginBottom: '20px' }}>
              Selected Categories ({selectedCategories.length})
            </h3>
            {selectedCategories.map((category) => (
              <div key={category.id} style={{
                padding: '15px',
                backgroundColor: '#f0f9ff',
                borderRadius: '8px',
                marginBottom: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <strong>{category.name}</strong>
                  <div style={{ color: '#667eea', fontSize: '0.9rem' }}>{category.section}</div>
                </div>
                <button
                  onClick={() => toggleCategorySelection(category.id, category.name, category.section)}
                  style={{
                    backgroundColor: '#e53e3e',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    padding: '5px 10px',
                    cursor: 'pointer'
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Proceed Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={proceedToNomination}
            disabled={selectedCategories.length === 0}
            style={{
              padding: '18px 40px',
              backgroundColor: selectedCategories.length === 0 ? '#a0aec0' : '#667eea',
              color: 'white',
              border: 'none',
              borderRadius: '30px',
              fontSize: '1.2rem',
              fontWeight: 700,
              cursor: selectedCategories.length === 0 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            Proceed to Nomination ({selectedCategories.length} selected)
          </button>
        </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default UpcomingAwards;